import { useMemo } from 'react';
import { PageShell, Card } from '@resource-kit/ui/react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderMermaidSVG } from 'beautiful-mermaid';

import operationsRunbook from '../../../docs/operations-runbook.md?raw';

// Mermaid theme using CSS variables for automatic dark/light mode support
const mermaidOptions = {
  bg: 'var(--color-card)',
  fg: 'var(--color-foreground)',
  accent: 'var(--color-primary)',
  muted: 'var(--color-muted-foreground)',
  surface: 'var(--color-surface-2)',
  border: 'var(--color-border)',
  font: 'inherit',
  transparent: true,
} as const;

/** Renders a mermaid code block synchronously via beautiful-mermaid. */
function MermaidBlock({ chart }: { chart: string }) {
  const svg = useMemo(() => {
    try {
      return renderMermaidSVG(chart, mermaidOptions);
    } catch {
      return null;
    }
  }, [chart]);

  if (!svg) {
    return (
      <pre className="text-xs bg-surface-2 border border-border rounded p-3 overflow-x-auto whitespace-pre">
        {chart}
      </pre>
    );
  }

  // SVG source is beautiful-mermaid rendering static bundled markdown — not user input
  return (
    <div
      className="my-4 flex justify-center overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// Static component map — no dependency on DocsPage state
const markdownComponents: Components = {
  code({ className, children, ...props }) {
    const lang = className?.replace('language-', '');
    if (lang === 'mermaid') {
      return <MermaidBlock chart={String(children).trim()} />;
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

const DocsPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageShell title={t('docs.title')}>
      {/* Markdown content — one runbook, so no chooser (see
          __tests__/architecture/docs-single-runbook.test.ts) */}
      <Card className="p-6 md:p-8">
        <div
          className="prose prose-sm dark:prose-invert max-w-none
          prose-headings:font-semibold prose-headings:tracking-tight
          prose-h1:text-xl prose-h1:border-b prose-h1:border-border prose-h1:pb-2
          prose-h2:text-lg prose-h2:mt-8
          prose-h3:text-base
          prose-table:text-sm
          prose-th:px-3 prose-th:py-2 prose-th:bg-surface-2 prose-th:font-medium
          prose-td:px-3 prose-td:py-2 prose-td:border-border
          prose-code:text-xs prose-code:bg-surface-2 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-pre:bg-surface-2 prose-pre:border prose-pre:border-border
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline
          prose-blockquote:border-primary prose-blockquote:text-muted-foreground
          prose-hr:border-border
          prose-li:marker:text-muted-foreground
        "
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {operationsRunbook}
          </ReactMarkdown>
        </div>
      </Card>
    </PageShell>
  );
};

export default DocsPage;
